import type { ProfileType } from './profileType';

export type RoomPaymentDisplayStatus = 'pending' | 'paid' | 'overdue';

export interface PaymentHistoryItem {
  id: string;
  title: string;
  amount: number;
  status: RoomPaymentDisplayStatus;
  paidDate: string;
  nextPaymentDate: string | null;
  description: string;
  paymentTerm?: string | null;
  billingPeriodStart?: string | null;
  roomNumber?: string | null;
  tenantName?: string | null;
}

export interface RoomPayHistoryRecord {
  id: string;
  transaction_id: string;
  title: string;
  amount: number;
  status: string;
  paid_date?: string | null;
  next_payment_date?: string | null;
  description: string;
  pay_room_code?: string | null;
  room_number?: string | null;
  property_id: number;
  tenant_name?: string | null;
  bank_reference?: string | null;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
  payment_term?: string | null;
  created_at?: string | null;
}

/** Chuẩn hóa: đã thanh toán | chờ thanh toán | quá hạn thanh toán. */
export function normalizePaymentDisplayStatus(raw: string): RoomPaymentDisplayStatus {
  const s = (raw || '').toLowerCase();
  if (s === 'paid' || s === 'completed') return 'paid';
  if (s === 'overdue') return 'overdue';
  return 'pending';
}

export function formatPaymentTerm(
  start?: string | null,
  end?: string | null,
): string | null {
  const formatPart = (value?: string | null): string | null => {
    if (!value) return null;
    const part = value.split('T')[0];
    const [year, month, day] = part.split('-');
    if (!year || !month || !day) return null;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  };

  const startLabel = formatPart(start);
  const endLabel = formatPart(end);
  if (startLabel && endLabel) {
    return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
  }
  return startLabel || endLabel;
}

export function mapRoomPayHistoryRecords(records: RoomPayHistoryRecord[]): PaymentHistoryItem[] {
  return records
    .filter((record) => {
      const raw = (record.status || '').toLowerCase();
      return raw !== 'expired' && raw !== 'cancelled' && raw !== 'failed';
    })
    .map((record) => {
      const status = normalizePaymentDisplayStatus(record.status);
      const roomNumber = (record.room_number || '').toString().trim() || null;
      return {
        id: record.id,
        title: record.title,
        amount: Number(record.amount) || 0,
        status,
        paidDate: status === 'paid' ? (record.paid_date || record.created_at || '') : (record.paid_date || ''),
        nextPaymentDate: record.next_payment_date || null,
        description: record.description,
        paymentTerm:
          record.payment_term
          || formatPaymentTerm(record.billing_period_start, record.billing_period_end),
        billingPeriodStart: record.billing_period_start || record.created_at || null,
        roomNumber,
        tenantName: record.tenant_name || null,
      };
    });
}

export function getPropertyAddress(property: any, contract?: any): string {
  const addressCandidates = [
    property?.address,
    property?.full_address,
    property?.location,
    property?.street_address,
    property?.street,
    property?.address_line,
    property?.formatted_address,
  ];

  const address = addressCandidates.find((value) => typeof value === 'string' && value.trim());
  if (typeof address === 'string' && address.trim()) {
    return address.trim();
  }

  const localityParts = [property?.ward_name, property?.district_name, property?.province_name]
    .filter((value): value is string => Boolean(value && String(value).trim()));
  if (localityParts.length > 0) {
    return localityParts.join(', ');
  }

  return property?.title || property?.name || 'Địa chỉ không có sẵn';
}

export function getMonthlyRentAmount(contract: any, property: any): number {
  const propertyPrice = property?.price ?? property?.monthly_price;
  if (propertyPrice != null && propertyPrice > 0) {
    return Number(propertyPrice);
  }
  if (contract.monthly_payment != null && contract.monthly_payment > 0) {
    return Number(contract.monthly_payment);
  }
  if (contract.amount != null && contract.total_payments > 0) {
    return Number(contract.amount) / Number(contract.total_payments);
  }
  return Number(contract.amount) || 0;
}

export function addMonthsToDateString(dateStr: string, months: number): string {
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return dateStr;

  const date = new Date(year, month - 1, day);
  date.setMonth(date.getMonth() + months);

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function toIsoDatePart(value?: string | null): string {
  if (!value) return '';
  return String(value).split('T')[0];
}

export function resolveRentalHistoryStartDate(contract: any): string {
  return toIsoDatePart(
    contract?.landlord_signed_at
    || contract?.tenant_signed_at
    || contract?.created_at
    || contract?.start_date
  );
}

export function resolveRentalHistoryEndDate(contract: any): string {
  const status = String(contract?.status || '').toLowerCase();
  const terminationStatus = String(contract?.termination_status || '').toLowerCase();
  const cancelled = status === 'cancelled'
    || terminationStatus === 'completed'
    || Boolean(contract?.early_termination)
    || Boolean(contract?.actual_end_date);

  if (cancelled) {
    return toIsoDatePart(
      contract?.actual_end_date
      || contract?.termination_requested_at
      || contract?.termination_approved_at
      || contract?.updated_at
    );
  }

  return toIsoDatePart(contract?.end_date);
}

export function resolveContractEndDate(contract: any): string {
  const startDate = contract.start_date?.split('T')[0];
  if (!startDate) {
    return contract.end_date?.split('T')[0] || '';
  }

  const durationMonths = Number(contract.total_payments) || 0;
  if (durationMonths > 0) {
    return addMonthsToDateString(startDate, durationMonths);
  }

  if (contract.monthly_payment > 0 && contract.amount > 0) {
    const derivedMonths = Math.round(Number(contract.amount) / Number(contract.monthly_payment));
    if (derivedMonths > 0) {
      return addMonthsToDateString(startDate, derivedMonths);
    }
  }

  return contract.end_date?.split('T')[0] || '';
}

function compareDateStrings(a: string, b: string): number {
  const [ay, am, ad] = a.split('T')[0].split('-').map(Number);
  const [by, bm, bd] = b.split('T')[0].split('-').map(Number);
  const dateA = new Date(ay, am - 1, ad).getTime();
  const dateB = new Date(by, bm - 1, bd).getTime();
  return dateA - dateB;
}

export function resolveRentalStatus(
  contract: any,
  property: any,
  profileType: ProfileType,
): string {
  const backendStatus = (contract.status || '').toLowerCase().trim();

  if (backendStatus === 'cancelled') return 'cancelled';
  if (backendStatus === 'completed') return 'completed';
  if (backendStatus === 'expired') return 'expired';
  if (backendStatus === 'active') return 'active';

  if (backendStatus === 'pending' || backendStatus === 'draft') {
    if (profileType === 'landlord' || profileType === 'business') {
      if (contract.tenant_name || property?.status === 'rented' || contract.landlord_signed) {
        return 'active';
      }
    }
    if ((contract.completed_payments || 0) > 0) return 'active';
    return 'pending';
  }

  const endDateStr = resolveContractEndDate(contract);
  if (endDateStr) {
    const [year, month, day] = endDateStr.split('-').map(Number);
    const endLocal = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (endLocal < today) {
      return 'expired';
    }
  }

  if (contract.tenant_signed && contract.landlord_signed) {
    return 'active';
  }

  return 'draft';
}

export function isLandlordManualContract(contract: any, profileType: ProfileType): boolean {
  if (profileType !== 'landlord' && profileType !== 'business') {
    return false;
  }

  const backendStatus = (contract.status || '').toLowerCase().trim();
  if (['cancelled', 'completed', 'expired'].includes(backendStatus)) {
    return false;
  }

  if (contract.tenant_name && (contract.landlord_signed || backendStatus === 'active')) {
    return true;
  }

  if (
    contract.tenant_name &&
    contract.landlord_id &&
    contract.tenant_id === contract.landlord_id
  ) {
    return true;
  }

  return false;
}

export function resolvePaymentStatus(
  contract: any,
  property: any,
  profileType: ProfileType,
): PaymentHistoryItem['status'] {
  if ((contract.completed_payments || 0) > 0) {
    return 'paid';
  }

  if (isLandlordManualContract(contract, profileType)) {
    return 'paid';
  }

  if (resolveRentalStatus(contract, property, profileType) === 'active') {
    return 'paid';
  }

  return 'pending';
}

export function resolvePaymentDates(
  contract: any,
  property: any,
  profileType: ProfileType,
): { paidDate: string; nextPaymentDate: string | null } {
  const startDate = contract.start_date?.split('T')[0] || '';
  const endDate = resolveContractEndDate(contract);
  const totalPayments = Number(contract.total_payments) || 0;
  const completedPayments = Number(contract.completed_payments) || 0;
  const isPaid = resolvePaymentStatus(contract, property, profileType) === 'paid';

  let effectiveCompleted = completedPayments;
  if (isPaid && effectiveCompleted === 0) {
    effectiveCompleted = 1;
  }

  const paidDate = startDate
    ? addMonthsToDateString(startDate, Math.max(effectiveCompleted - 1, 0))
    : '';

  if (!paidDate || !endDate) {
    return { paidDate, nextPaymentDate: null };
  }

  if (totalPayments > 0 && effectiveCompleted >= totalPayments) {
    return { paidDate, nextPaymentDate: null };
  }

  const nextPaymentDate = addMonthsToDateString(paidDate, 1);
  if (compareDateStrings(nextPaymentDate, endDate) > 0) {
    return { paidDate, nextPaymentDate: null };
  }

  return { paidDate, nextPaymentDate };
}

export function buildPaymentHistoryItems(
  contracts: any[],
  propertyMap: Map<number, any>,
  profileType: ProfileType,
  getTitle: (contract: any, property: any) => string,
): PaymentHistoryItem[] {
  const items: PaymentHistoryItem[] = [];

  contracts
    .filter((contract) => contract.status !== 'cancelled')
    .forEach((contract) => {
      const property = propertyMap.get(contract.property_id);
      const monthlyRent = getMonthlyRentAmount(contract, property);
      const paymentStatus = resolvePaymentStatus(contract, property, profileType);
      const { paidDate, nextPaymentDate } = resolvePaymentDates(contract, property, profileType);
      const title = getTitle(contract, property);
      const roomNumber = (
        property?.room_number
        || property?.unit_number
        || contract?.room_number
        || contract?.unit_number
        || ''
      ).toString().trim() || null;
      const depositAmount = Number(contract.deposit) || 0;
      const hasInitialPayment =
        (contract.completed_payments || 0) > 0 || isLandlordManualContract(contract, profileType);

      items.push({
        id: `payment-${contract.id}`,
        title,
        amount: monthlyRent,
        status: paymentStatus,
        paidDate,
        nextPaymentDate,
        description: 'Tiền thuê tháng đầu',
        paymentTerm: formatPaymentTerm(paidDate, nextPaymentDate || addMonthsToDateString(paidDate, 1)),
        billingPeriodStart: paidDate || null,
        roomNumber,
        tenantName: (contract.tenant_name || '').trim() || null,
      });

      if (depositAmount > 0 && hasInitialPayment) {
        items.push({
          id: `deposit-${contract.id}`,
          title,
          amount: depositAmount,
          status: 'paid',
          paidDate: contract.start_date?.split('T')[0] || paidDate,
          nextPaymentDate: null,
          description: 'Tiền cọc',
          paymentTerm: formatPaymentTerm(
            contract.start_date?.split('T')[0] || paidDate,
            contract.start_date?.split('T')[0] || paidDate,
          ),
          billingPeriodStart: contract.start_date?.split('T')[0] || paidDate || null,
          roomNumber,
          tenantName: (contract.tenant_name || '').trim() || null,
        });
      }
    });

  return items;
}

/** Tổng tiền đã thu trong năm — bằng tổng amount các dòng status=paid trong lịch sử thanh toán */
export function calculateTotalReceivedInYear(
  items: PaymentHistoryItem[],
  year: number = new Date().getFullYear(),
): number {
  return items.reduce((total, item) => {
    if (item.status !== 'paid' || !item.paidDate) return total;
    const datePart = item.paidDate.split('T')[0];
    const paymentYear = Number(datePart.split('-')[0]);
    if (!Number.isFinite(paymentYear) || paymentYear !== year) return total;
    return total + (Number(item.amount) || 0);
  }, 0);
}

/** Tháng kỳ thu (billing_period_start) hoặc tháng paid_date nếu không có kỳ */
export function getRentBillingMonthKey(record: RoomPayHistoryRecord): string | null {
  const periodStart = record.billing_period_start?.split('T')[0];
  if (periodStart) {
    const [year, month] = periodStart.split('-');
    if (year && month) return `${year}-${month}`;
  }

  if ((record.status || '').toLowerCase() !== 'paid') return null;
  const paidDate = record.paid_date?.split('T')[0];
  if (!paidDate) return null;
  const [year, month] = paidDate.split('-');
  if (!year || !month) return null;
  return `${year}-${month}`;
}

/** Tổng tiền nhà đã thanh toán trong tháng — theo kỳ thu (ưu tiên billing_period_start) */
export function calculateTotalReceivedInMonth(
  records: RoomPayHistoryRecord[],
  year: number = new Date().getFullYear(),
  month: number = new Date().getMonth() + 1,
): number {
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  return records.reduce((total, record) => {
    if ((record.status || '').toLowerCase() !== 'paid') return total;
    if (getRentBillingMonthKey(record) !== monthKey) return total;
    return total + (Number(record.amount) || 0);
  }, 0);
}

export function formatRentMonthLabel(year: number, month: number): string {
  return `Tháng ${month}/${year}`;
}

/** 12 tháng gần nhất — cùng công thức với ô "Tiền nhà đã thu". */
export function buildMonthlyReceivedSeries(
  records: RoomPayHistoryRecord[],
  now: Date = new Date(),
): number[] {
  return Array.from({ length: 12 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (11 - index), 1);
    return calculateTotalReceivedInMonth(records, date.getFullYear(), date.getMonth() + 1);
  });
}

/** Alias — cùng công thức với calculateTotalReceivedInYear */
export const sumPaidPaymentHistoryInYear = calculateTotalReceivedInYear;

export function getContractsRoleForProfile(profileType: ProfileType): string {
  if (profileType === 'tenant') return 'tenant';
  if (profileType === 'business') return 'business';
  return 'landlord';
}
